import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Collapsible, PdfShower } from "../../components";
import { STEPS } from "../constants";
import { useOrderManagement } from "../useOrderManagement";
import { SectionTrigger } from "./SectionTrigger";
import { ToggleButton } from "../../listings/atoms";
import { Button } from "../../components/ui";
import SendInvoiceModal from "./SendInvoiceModal";
import "./invoice.scss";

// Format an ISO timestamp in UK time (BST in summer, GMT in winter — the tz
// label is derived automatically so it's always accurate), e.g. "2 Jul 2026,
// 14:32 BST".
const formatSentAt = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

export function Invoice({
  step,
  lastStep,
  getPdf,
  getPdfUrl,
  order_id = null,
  goToCommissions,
  initToggleStatus = false,
  existUserCompany,
}) {
  const [pdf, setPdf] = useState();
  // The viewer renders the PDF in an <object data=…> — a browser sub-resource
  // request that carries NO auth token, so a bare /api/pdfs/{id} 403s for any
  // file gated by the backend's FileAccessPolicy. Every invoice endpoint now
  // returns a signed URL alongside the id; render that. `pdf` stays the numeric
  // id for the send-invoice call.
  const [pdfUrl, setPdfUrl] = useState();
  const [toggleStatus, setToggleStatus] = useState(initToggleStatus);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sentAt, setSentAt] = useState(null);
  const fetchData = async () => {
    const result = await axios.get(`/api/order-management/${order_id}/get-pdf`);

    setPdf(result?.data?.fileId);
    setPdfUrl(result?.data?.fileUrl);
  };
  const onOpen = async () => {
    const isPdfNotCreated = !getPdf || getPdf == "";

    if (isPdfNotCreated && order_id) fetchData();
    if (!isPdfNotCreated) {
      setPdf(getPdf);
      setPdfUrl(getPdfUrl);
    }
  };
  const toggleStatusManagement = () => {
    setToggleStatus((prev) => !prev);
  };
  useEffect(() => {
    const isPdfNotCreated = !getPdf || getPdf == "";

    if (!isPdfNotCreated) {
      setPdf(getPdf);
      setPdfUrl(getPdfUrl);
    }
  }, [getPdf, getPdfUrl]);
  const updateOrderToggleStatus = async () => {
    const result = await axios.post(
      `/api/order-management/${order_id}/update-toggle-status`,
      {
        orderId: order_id,
        toggleStatus,
      }
    );
    if (toggleStatus === null && result) {
      setToggleStatus(result?.toggle_status);
    }
  };

  useEffect(() => {
    const updateInvoicePdf = async () => {
      await updateOrderToggleStatus();
      const response = await axios.put(
        `/api/order-management/${order_id}/update-invoice`
      );

      if (response?.data?.fileId) {
        setPdf(response.data.fileId);
        setPdfUrl(response.data.fileUrl);
      }
    };

    if (order_id) updateInvoicePdf();
  }, [toggleStatus]);

  const { isInline, setStep } = useOrderManagement();

  if (lastStep < STEPS.INVOICE) {
    return null;
  }

  return (
    <Collapsible
      trigger={<SectionTrigger icon="invoice" label="Invoice" />}
      open={step === STEPS.INVOICE}
      handleTriggerClick={
        isInline
          ? () => setStep(step === STEPS.INVOICE ? -1 : STEPS.INVOICE)
          : undefined
      }
      onOpening={() => onOpen()}
    >
      <div className="order-invoice mt-4">
        {pdfUrl || pdf ? (
          <PdfShower fileId={pdfUrl || pdf} />
        ) : (
          <div className="placeholder"> </div>
        )}

        <div className="d-flex justify-content-between">
          <div className="text-left mt-5">
            <ToggleButton
              label="Use Company Name"
              disabled={!existUserCompany}
              checked={toggleStatus}
              onChange={toggleStatusManagement}
            />
          </div>
          <div className="action-buttons text-right mt-5">
            <Button
              variant="secondary"
              data-btn="invoice-open-send"
              onClick={() => setShowSendModal(true)}
            >
              Send invoice
            </Button>

            <Button variant="primary" data-btn="invoice-confirm" onClick={goToCommissions}>
              Proceed
            </Button>
            {sentAt && (
              <div className="invoice-sent-note mt-2">
                Invoice sent — {formatSentAt(sentAt)}
              </div>
            )}
          </div>
        </div>
      </div>
      {showSendModal && (
        <SendInvoiceModal
          orderId={order_id}
          fileId={pdf}
          onClose={() => setShowSendModal(false)}
          onSent={(sent_at) => {
            setShowSendModal(false);
            // Keep the INVOICE section open so the rep sees the confirmation;
            // PROCEED advances to Commissions when they're ready.
            setSentAt(sent_at || new Date().toISOString());
          }}
        />
      )}
    </Collapsible>
  );
}
